import React from "react";

import PdfReportPage from "../../../Common/PdfExport/components/PdfReportPage";
import PdfSectionImageTitle from "../../../Common/PdfExport/components/PdfSectionImageTitle";

import SettlementPdfFeeScheduleSection from "./SettlementPdfFeeScheduleSection";
import SettlementPdfHeaderGrid from "./SettlementPdfHeaderGrid";
import SettlementPdfMedicalTable from "./SettlementPdfMedicalTable";
import SettlementPdfSummaryTable from "./SettlementPdfSummaryTable";

const SECTION_KEYS = {
  financialSummary: "financialSummary",
  medicalBreakdown: "medicalBreakdown",
  settlementStatement: "settlementStatement",
};

function buildMedicalSummaryRows(formatCurrency, summary, isProvider) {
  const rows = [
    { title: "Total Medical Bills", value: formatCurrency(summary.totalMedicalBills) },
    { title: "Total Settled Amount", value: formatCurrency(summary.totalSettledAmount) },
  ];

  if (!isProvider) {
    rows.push(
      { title: "Total Medical Share", value: formatCurrency(summary.totalMedicalShare) },
      {
        title: "Remaining Unclaimed",
        value: formatCurrency(summary.remainingUnclaimed),
        modifier:
          Number(summary.remainingUnclaimed || 0) > 0
            ? "settlement-pdf-summary-card--success"
            : "settlement-pdf-summary-card--failure",
      }
    );
  }

  return rows;
}

function buildMedicalPages(records) {
  const firstMedicalPageRows = 7;
  const continuationMedicalPageRows = 15;

  if (!records.length) {
    return [[]];
  }

  const pages = [records.slice(0, firstMedicalPageRows)];
  let offset = firstMedicalPageRows;

  while (offset < records.length) {
    pages.push(records.slice(offset, offset + continuationMedicalPageRows));
    offset += continuationMedicalPageRows;
  }

  return pages;
}

function isSectionSelected(selectedSections, sectionKey) {
  if (!selectedSections || Object.keys(selectedSections).length === 0) {
    return true;
  }

  return Boolean(selectedSections[sectionKey]);
}

export default function SettlementPdfDocument({
  bannerUrl,
  feeSchedule,
  formatCurrency,
  headerFields,
  isFeeScheduleUnavailable,
  isProvider,
  medicalBreakdownRecords,
  medicalSummary,
  pieChartUrl,
  selectedSections,
  sectionTitleUrls,
  settlementAmountPresent,
}) {
  const medicalPages = buildMedicalPages(medicalBreakdownRecords);
  const showSettlementStatement = isSectionSelected(selectedSections, SECTION_KEYS.settlementStatement);
  const showMedicalBreakdown = isSectionSelected(selectedSections, SECTION_KEYS.medicalBreakdown);
  const showFinancialSummary = isSectionSelected(selectedSections, SECTION_KEYS.financialSummary);

  return (
    <>
      {(showSettlementStatement || showMedicalBreakdown) ? (
        <PdfReportPage bannerUrl={bannerUrl}>
          {showSettlementStatement ? (
            <>
              <div className="pdf-report-section">
                <h1 className="settlement-pdf-document-title">Settlement Statement</h1>
              </div>
              <div className="pdf-report-section">
                <PdfSectionImageTitle alt="Client Details" imageUrl={sectionTitleUrls.settlement} />
                <SettlementPdfHeaderGrid headerFields={headerFields} />
              </div>
            </>
          ) : null}
          {showMedicalBreakdown ? (
            <div className="pdf-report-section">
              <PdfSectionImageTitle alt="Medical Breakdown" imageUrl={sectionTitleUrls.medical} />
              <SettlementPdfMedicalTable formatCurrency={formatCurrency} records={medicalPages[0]} />
            </div>
          ) : null}
        </PdfReportPage>
      ) : null}
      {showMedicalBreakdown ? medicalPages.slice(1).map((records, index) => (
        <PdfReportPage bannerUrl={bannerUrl} key={`medical-page-${index + 2}`}>
          <div className="pdf-report-section">
            <PdfSectionImageTitle alt="Medical Breakdown" imageUrl={sectionTitleUrls.medical} />
            <SettlementPdfMedicalTable formatCurrency={formatCurrency} records={records} />
          </div>
        </PdfReportPage>
      )) : null}
      {(showFinancialSummary && settlementAmountPresent && !isFeeScheduleUnavailable) ? (
        <PdfReportPage bannerUrl={bannerUrl}>
          <div className="pdf-report-section">
            <PdfSectionImageTitle alt="Total Medical Reduction" imageUrl={sectionTitleUrls.reduction} />
            <SettlementPdfSummaryTable
              rows={buildMedicalSummaryRows(formatCurrency, medicalSummary, isProvider)}
            />
          </div>
          <SettlementPdfFeeScheduleSection
              feeSchedule={feeSchedule}
              formatCurrency={formatCurrency}
              pieChartUrl={pieChartUrl}
              titleUrl={sectionTitleUrls.fee}
            />
        </PdfReportPage>
      ) : null}
    </>
  );
}
